// ════════════════════════════════════════════════════════════════════════════
// components/CRM/PipelineKanban.jsx
// ✅ CORREGIDO: Mejoras estéticas, corrección de bugs y mejor UX
// ════════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  Avatar,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Collapse,
  Card,
  CardContent,
  Badge,
  alpha,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,

  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarTodayIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,

  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon
} from '@mui/icons-material';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {  SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  getOportunidades, 
  getOportunidadById, 
  moverEtapaOportunidad,
 
} from '../../services/crmService';
import SeguimientosModal from './modales/SeguimientosModal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TransicionEtapaModal from './modales/TransicionEtapaModal';


// ════════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN DE ETAPAS - MEJORADA ESTÉTICAMENTE
// ════════════════════════════════════════════════════════════════════════════

const ETAPAS = [
  { 
    id: 'contacto_inicial', 
    label: '📞 Contacto Inicial', 
    color: '#3b82f6', 
    bgcolor: '#dbeafe',
    icon: <PhoneIcon />,
    descripcion: 'Primer contacto con el prospecto'
  },
  { 
    id: 'necesidad_validada', 
    label: '🔍 Necesidad Validada', 
    color: '#8b5cf6', 
    bgcolor: '#ede9fe',
    icon: <CheckCircleIcon />,
    descripcion: 'Se validaron los requerimientos'
  },
  {
    id: 'demo',
    label: '🎯 Demo',
    color: '#06b6d4',
    bgcolor: '#cffafe',
    icon: <PeopleIcon />,
    descripcion: 'Demostración realizada con el prospecto'
  },
  {
    id: 'propuesta_enviada',
    label: '📄 Propuesta Enviada',
    color: '#f59e0b',
    bgcolor: '#fef3c7',
    icon: <AttachFileIcon />,
    descripcion: 'Propuesta entregada al cliente'
  },
  { 
    id: 'negociacion', 
    label: '🤝 En Negociación', 
    color: '#ec4899', 
    bgcolor: '#fce7f3',
    icon: <PeopleIcon />,
    descripcion: 'Negociación de términos y condiciones'
  },
  { 
    id: 'cerrado_ganado', 
    label: '✅ Cerrado - Ganado', 
    color: '#10b981', 
    bgcolor: '#d1fae5',
    icon: <MonetizationOnIcon />,
    descripcion: 'Oportunidad convertida en cliente'
  },
  { 
    id: 'cerrado_perdido', 
    label: '❌ Cerrado - Perdido', 
    color: '#ef4444', 
    bgcolor: '#fee2e2',
    icon: <CloseIcon />,
    descripcion: 'Oportunidad no concretada'
  }
];

const ETAPAS_CONFIG = {
  contacto_inicial: { next: 'necesidad_validada' },
  necesidad_validada: { next: 'propuesta_enviada' }, // overridden for saas via getNextEtapa
  demo: { next: 'propuesta_enviada' },
  propuesta_enviada: { next: 'negociacion' },
  negociacion: { next: 'cerrado_ganado' },
  cerrado_ganado: { next: null },
  cerrado_perdido: { next: null }
};

// Para saas: necesidad_validada → demo en lugar de propuesta_enviada
const getNextEtapa = (oportunidad) => {
  if (oportunidad.etapa === 'necesidad_validada' && oportunidad.tipo_negocio === 'saas') {
    return 'demo';
  }
  return ETAPAS_CONFIG[oportunidad.etapa]?.next || null;
};

// ════════════════════════════════════════════════════════════════════════════
// COMPONENTE TARJETA DE OPORTUNIDAD (Draggable) - MEJORADO
// ════════════════════════════════════════════════════════════════════════════

const OportunidadCard = ({ oportunidad, onClick, onMoverSiguiente, navigate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: oportunidad.id.toString() });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab'
  };
  
  const probabilidad = oportunidad.probabilidad || 0;
  const diasDesdeActualizacion = Math.floor(
    (new Date() - new Date(oportunidad.actualizado_en)) / (1000 * 60 * 60 * 24)
  );
  const necesitaAtencion = diasDesdeActualizacion > 7;
  
  const getProbabilidadColor = (prob) => {
    if (prob > 70) return '#10b981';
    if (prob > 40) return '#f59e0b';
    return '#ef4444';
  };
  
  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...attributes}
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 2,
        border: `1px solid ${isDragging ? '#3b82f6' : '#e2e8f0'}`,
        borderLeft: `4px solid ${getProbabilidadColor(probabilidad)}`,
        boxShadow: isDragging ? '0 8px 16px rgba(59, 130, 246, 0.15)' : '0 1px 3px rgba(0,0,0,0.08)',
        bgcolor: isDragging ? '#f8fafc' : 'white',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          borderColor: '#cbd5e1'
        }
      }}
    >
      {/* Header con drag handle */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <Box 
          {...listeners} 
          sx={{ 
            cursor: isDragging ? 'grabbing' : 'grab', 
            color: '#94a3b8',
            mt: 0.5,
            '&:hover': { color: '#3b82f6' }
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="subtitle2" 
            fontWeight="bold" 
            sx={{ 
              color: '#1e293b',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {oportunidad.prospecto?.nombre || 'Sin nombre'}
          </Typography>
          
          {oportunidad.prospecto?.empresa && (
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.5
              }}
            >
              <BusinessIcon fontSize="inherit" />
              {oportunidad.prospecto.empresa}
            </Typography>
          )}
        </Box>
        
        {necesitaAtencion && (
          <Tooltip title={`Sin actividad hace ${diasDesdeActualizacion} días`}>
            <Chip 
              icon={<WarningIcon />}
              label={diasDesdeActualizacion}
              size="small"
              color="error"
              sx={{ height: 24, fontSize: '0.7rem' }}
            />
          </Tooltip>
        )}
      </Box>
      
      {/* Valor estimado */}
      <Box sx={{ mb: 1.5, p: 1, bgcolor: '#f8fafc', borderRadius: 1.5 }}>
        <Typography variant="caption" display="flex" alignItems="center" color="text.secondary" sx={{ mb: 0.5 }}>
          <MonetizationOnIcon fontSize="inherit" sx={{ mr: 0.5 }} />
          Valor Estimado
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="#059669">
          ${Number(oportunidad.valor_estimado || 0).toLocaleString('es-DO', { 
            minimumFractionDigits: 0,
            maximumFractionDigits: 2 
          })}
        </Typography>
      </Box>
      
      {/* Probabilidad */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Probabilidad
          </Typography>
          <Typography 
            variant="caption" 
            fontWeight="bold" 
            sx={{ color: getProbabilidadColor(probabilidad) }}
          >
            {probabilidad}%
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={probabilidad} 
          sx={{ 
            height: 6, 
            bgcolor: '#e2e8f0',
            borderRadius: 3,
            '& .MuiLinearProgress-bar': { 
              bgcolor: getProbabilidadColor(probabilidad),
              borderRadius: 3,
              transition: 'all 0.3s ease'
            }
          }} 
        />
      </Box>
      
      {/* Responsable */}
      {oportunidad.responsable && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Avatar 
            sx={{ 
              width: 28, 
              height: 28, 
              fontSize: '0.875rem',
              bgcolor: '#3b82f6',
              color: 'white'
            }}
          >
            {oportunidad.responsable.nombre?.charAt(0) || 'U'}
          </Avatar>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Responsable
            </Typography>
            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
              {oportunidad.responsable.nombre}
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Contacto rápido */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          📞 Contacto
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
          {oportunidad.prospecto?.telefono || 'Sin teléfono'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          {oportunidad.prospecto?.email ? `${oportunidad.prospecto.email.substring(0, 15)}...` : ''}
        </Typography>
      </Box>
      
{/* Botón Ver Propuesta - Se muestra si tiene propuesta con lineas O partidas */}
{oportunidad.propuesta && (oportunidad.propuesta.lineas?.length > 0 || oportunidad.propuesta.partidas?.length > 0) && (
  <Button
    size="small"
    variant="contained"
    startIcon={<DescriptionIcon />}
    onClick={(e) => {
      e.stopPropagation();
      
      console.log('📤 ENVIANDO DATOS:', {
        oportunidadId: oportunidad.id,
        etapa: oportunidad.etapa,
        propuestaOriginal: oportunidad.propuesta,
        tieneLineas: oportunidad.propuesta.lineas?.length,
        tienePartidas: oportunidad.propuesta.partidas?.length,
        tieneCierreDetalle: !!oportunidad.cierre_detalle,
        tienePartidasPactadas: oportunidad.cierre_detalle?.partidas_pactadas?.length
      });
      
      // Asegurar que propuesta sea un objeto
      let propuestaParaEnviar = oportunidad.propuesta;
      
      // Si por alguna razón es string, parsearlo
      if (typeof propuestaParaEnviar === 'string') {
        try {
          propuestaParaEnviar = JSON.parse(propuestaParaEnviar);
        } catch (err) {
          console.error('❌ Error parseando propuesta:', err);
          return;
        }
      }
      
      // 🎯 CLAVE: Si está en negociación o cerrado_ganado, usar precios negociados
      let lineasFinales = propuestaParaEnviar.lineas || [];
      
      // Si está en negociación y tiene partidas pactadas, usar esas
      if ((oportunidad.etapa === 'negociacion' || oportunidad.etapa === 'cerrado_ganado') && 
          oportunidad.cierre_detalle?.partidas_pactadas?.length > 0) {
        
        console.log('✅ Usando precios NEGOCIADOS de cierre_detalle');
        lineasFinales = oportunidad.cierre_detalle.partidas_pactadas.map(p => ({
          codigo: p.linea_id || '',
          nombre: p.descripcion || '',
          descripcion: p.descripcion || '',
          cantidad: p.cantidad,
          unidad: 'und',
          precio_unitario: p.precio_pactado,  // ← Precio NEGOCIADO
          total: p.total_pactado,
          modificado: p.modificado
        }));
      }
      
      // Asegurar que lineas sea array
      if (!Array.isArray(lineasFinales)) {
        console.warn('⚠️ lineas no es array, usando array vacío');
        lineasFinales = [];
      }
      
      // Incluir prospecto en la propuesta
      const propuestaConProspecto = {
        ...propuestaParaEnviar,
        lineas: lineasFinales,  // ← Usar lineasFinales (con precios correctos)
        prospecto: oportunidad.prospecto
      };
      
      navigate(`/crm/cotizaciones/${oportunidad.id}`, {
        state: {
          propuestaData: propuestaConProspecto,
          prospectoData: oportunidad.prospecto,
          oportunidadId: oportunidad.id
        }
      });
    }}
    sx={{
      width: '100%',
      mb: 1.5,
      bgcolor: '#f59e0b',
      color: 'white',
      fontSize: '0.75rem',
      fontWeight: 'bold',
      '&:hover': {
        bgcolor: '#d97706'
      }
    }}
  >
    📄 Ver Propuesta
  </Button>
)}
      
      {/* Botones de acción */}
      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onClick(oportunidad);
          }}
          sx={{ 
            flex: 1, 
            borderColor: '#3b82f6', 
            color: '#3b82f6',
            fontSize: '0.75rem',
            '&:hover': {
              bgcolor: '#eff6ff',
              borderColor: '#1d4ed8'
            }
          }}
        >
          Ver detalles
        </Button>
        
        {getNextEtapa(oportunidad) && (
          <Tooltip title={`Mover a ${ETAPAS[ETAPAS.findIndex(e => e.id === getNextEtapa(oportunidad))]?.label || ''}`}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onMoverSiguiente(oportunidad, getNextEtapa(oportunidad));
              }}
              sx={{ 
                bgcolor: '#3b82f6', 
                color: 'white',
                '&:hover': { 
                  bgcolor: '#2563eb',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
};
const KanbanColumn = ({ etapa, oportunidades, onMoverSiguiente, onCardClick, navigate }) => {
  const [collapsed, setCollapsed] = useState(false);
  
  const valorTotal = oportunidades.reduce((sum, opp) => sum + parseFloat(opp.valor_estimado || 0), 0);
  
  const oportunidadesVencidas = oportunidades.filter(opp => {
    const diasSinActividad = Math.floor(
      (new Date() - new Date(opp.actualizado_en)) / (1000 * 60 * 60 * 24)
    );
    return diasSinActividad > 7;
  });
  
  return (
    <Paper 
      sx={{ 
        minWidth: 320, 
        maxWidth: 360,
        height: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: '#fafafa'
      }}
    >
      {/* HEADER */}
      <Box 
        sx={{ 
          p: 2, 
          bgcolor: etapa.color, 
          color: 'white',
          flexShrink: 0
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            {etapa.label}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => setCollapsed(!collapsed)}
            sx={{ color: 'white' }}
          >
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Chip 
            label={`${oportunidades.length} ops`} 
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
          <Typography variant="body2">
            ${(valorTotal / 1000000).toFixed(1)}M
          </Typography>
        </Box>
      </Box>
      
      {/* CONTENIDO - SIN COLLAPSE, CON CONDICIONAL SIMPLE */}
      {!collapsed && (
        <Box 
          sx={{ 
            flex: 1,
            overflowY: 'auto',
            p: 2,
            bgcolor: '#f5f5f5'
          }}
        >
          {oportunidades.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <Typography>No hay oportunidades</Typography>
            </Box>
          ) : (
            oportunidades.map((oportunidad) => (
              <OportunidadCard
                key={oportunidad.id}
                oportunidad={oportunidad}
                onClick={onCardClick}
                onMoverSiguiente={onMoverSiguiente}
                navigate={navigate}
              />
            ))
          )}
        </Box>
      )}
    </Paper>
  );
};
// ════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL - COMPLETAMENTE CORREGIDO
// ════════════════════════════════════════════════════════════════════════════

const PipelineKanban = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [oportunidades, setOportunidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [modalDetalleOpen, setModalDetalleOpen] = useState(false);
  const [oportunidadSeleccionadaData, setOportunidadSeleccionadaData] = useState(null);
  const [oportunidadSeleccionadaId, setOportunidadSeleccionadaId] = useState(null);
  const [seguimientoModalOpen, setSeguimientoModalOpen] = useState(false);
  const [transicionModalOpen, setTransicionModalOpen] = useState(false);
  const [transicionData, setTransicionData] = useState({ oportunidad: null, etapaNueva: null });
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor)
  );
  
  useEffect(() => {
    cargarOportunidades();
  }, []);
 // PipelineKanban.jsx

const cargarOportunidades = async () => {
  setLoading(true);
  setError(null);
  
  try {
    const response = await getOportunidades();
    
    // 🔍 ANALICEMOS LA RUTA:
    // response.data -> Es el JSON del servidor { success: true, data: { oportunidades, count } }
    // response.data.data -> Es el objeto { oportunidades: [...], count: 3 }
    // response.data.data.oportunidades -> ¡AQUÍ ESTÁ EL ARRAY!
    
    const arrayOportunidades = response.data?.data?.oportunidades || [];
    
    // Seguridad: Si por alguna razón el backend enviara el array directo en el futuro
    const datosFinales = Array.isArray(arrayOportunidades) 
      ? arrayOportunidades 
      : (Array.isArray(response.data?.data) ? response.data.data : []);

    setOportunidades(datosFinales);
    
    console.log("Pipeline cargado con éxito. Cantidad:", datosFinales.length);
  } catch (err) {
    console.error('Error cargando oportunidades:', err);
    setError('Error al cargar el pipeline. Verifica tu conexión.');
    toast.error('❌ Error al cargar el pipeline');
    setOportunidades([]); // Evitamos que el componente explote
  } finally {
    setLoading(false);
  }
};
  
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };
  
 const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oportunidad = oportunidades.find(o => o.id.toString() === active.id);
      const destinoEtapa = ETAPAS.find(e => e.id === over.id)?.id;
      
      if (oportunidad && destinoEtapa && oportunidad.etapa !== destinoEtapa) {
        // 🚀 EN LUGAR DE MOVER DIRECTO, ABRIMOS EL MODAL
        setTransicionData({ oportunidad, etapaDestino: destinoEtapa });
        setTransicionModalOpen(true);
      }
    }
    setActiveId(null);
  };


  
 const handleMoverSiguiente = (oportunidad, nuevaEtapa) => {
  // 1. Guardamos los datos de hacia dónde queremos ir
  setTransicionData({
    oportunidad: oportunidad,
    etapaDestino: nuevaEtapa
  });
  
  // 2. Abrimos el modal inmersivo (él se encargará de llamar a la API)
  setTransicionModalOpen(true);
};
  // En PipelineKanban.jsx - Modificar handleCardClick
const handleCardClick = (oportunidad) => {
  const prospectoId = oportunidad.prospecto_id || oportunidad.prospecto?.id;

  if (prospectoId) {
    // 🚀 Pasar la etapa actual como state en la navegación
    navigate(`/crm/prospectos/${prospectoId}`, {
      state: { 
        etapaPipeline: oportunidad.etapa,
        oportunidadId: oportunidad.id,
        returnTo: '/crm/pipeline'
      }
    });
  } else {
    toast.error('❌ Error: No se pudo identificar al prospecto');
  }
};
  const handleCloseDetalleModal = () => {
    setModalDetalleOpen(false);
    setTimeout(() => {
      setOportunidadSeleccionadaData(null);
      setOportunidadSeleccionadaId(null);
    }, 300);
  };
  
  const handleOpenSeguimientoModal = () => {
    if (oportunidadSeleccionadaId) {
      setSeguimientoModalOpen(true);
    } else {
      toast.error('❌ No hay oportunidad seleccionada');
    }
  };
  
  const handleRefresh = () => {
    cargarOportunidades();
    toast.info('🔄 Actualizando datos...');
  };
  
  const handleCloseSeguimientoModal = () => {
    setSeguimientoModalOpen(false);
  };
  
  // Agrupar oportunidades por etapa
  const oportunidadesPorEtapa = ETAPAS.map(etapa => ({
    ...etapa,
    items: oportunidades.filter(o => o.etapa === etapa.id)
  }));
  
  // Calcular estadísticas generales
  const estadisticas = {
    totalOportunidades: oportunidades.length,
    valorTotal: oportunidades.reduce((sum, o) => sum + (parseFloat(o.valor_estimado) || 0), 0),
    oportunidadesVencidas: oportunidades.filter(o => {
      const dias = Math.floor((new Date() - new Date(o.actualizado_en)) / (1000 * 60 * 60 * 24));
      return dias > 7;
    }).length
  };
  
  return (
    <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header mejorado */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        bgcolor: 'white',
        p: 3,
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 0.5 }}>
            📊 Pipeline de Ventas - Vista Kanban
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Arrastra y suelta las oportunidades para moverlas entre etapas
          </Typography>
          
          {/* Estadísticas rápidas */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Chip 
              icon={<TrendingUpIcon />}
              label={`${estadisticas.totalOportunidades} oportunidades`}
              size="small"
              sx={{ bgcolor: '#eff6ff', color: '#1d4ed8' }}
            />
            <Chip 
              icon={<MonetizationOnIcon />}
              label={`$${Math.round(estadisticas.valorTotal).toLocaleString('es-DO')}`}
              size="small"
              sx={{ bgcolor: '#d1fae5', color: '#065f46' }}
            />
            {estadisticas.oportunidadesVencidas > 0 && (
              <Chip 
                icon={<WarningIcon />}
                label={`${estadisticas.oportunidadesVencidas} vencidas`}
                size="small"
                color="error"
              />
            )}


          </Box>

          
        </Box>
        
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
          sx={{ 
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#2563eb' },
            borderRadius: 2,
            px: 3,
            py: 1
          }}
        >
          {loading ? 'Cargando...' : 'Actualizar'}
        </Button>
      </Box>
      
      {/* Mensaje de error mejorado */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={cargarOportunidades}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      
      {/* Vista Kanban */}
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          py: 10,
          bgcolor: 'white',
          borderRadius: 2
        }}>
          <CircularProgress sx={{ mb: 2, color: '#3b82f6' }} />
          <Typography variant="body1" color="text.secondary">
            Cargando pipeline...
          </Typography>
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            overflowX: 'auto', 
            pb: 2,
            minHeight: 'calc(100vh - 200px)',
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f5f9',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#cbd5e1',
              borderRadius: 4,
              '&:hover': {
                background: '#94a3b8',
              },
            },
          }}>
            {oportunidadesPorEtapa.map((columna) => (
              <KanbanColumn
                key={columna.id}
                etapa={columna}
                oportunidades={columna.items}
                onMoverSiguiente={handleMoverSiguiente}
                onCardClick={handleCardClick}
                navigate={navigate}
              />
            ))}
          </Box>
        </DndContext>
      )}
      
      {/* Modal de detalles de oportunidad - MEJORADO */}
      <Dialog 
        open={modalDetalleOpen} 
        onClose={handleCloseDetalleModal} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#3b82f6', 
          color: 'white',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                📋 Detalles de la Oportunidad
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {oportunidadSeleccionadaData?.prospecto?.nombre || 'Sin nombre'} • {oportunidadSeleccionadaData?.prospecto?.empresa || '-'}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDetalleModal} size="small" sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 3 }}>
          {oportunidadSeleccionadaData && (
            <Grid container spacing={3}>
              {/* Información principal */}
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#64748b" sx={{ mb: 2 }}>
                      📊 Información General
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Etapa Actual
                      </Typography>
                      <Chip
                        label={ETAPAS.find(e => e.id === oportunidadSeleccionadaData.etapa)?.label || oportunidadSeleccionadaData.etapa}
                        sx={{ 
                          bgcolor: ETAPAS.find(e => e.id === oportunidadSeleccionadaData.etapa)?.bgcolor,
                          color: ETAPAS.find(e => e.id === oportunidadSeleccionadaData.etapa)?.color,
                          fontWeight: 'bold',
                          py: 1
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        💰 Valor Estimado
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" color="#059669">
                        ${Number(oportunidadSeleccionadaData.valor_estimado || 0).toLocaleString('es-DO', { 
                          minimumFractionDigits: 2 
                        })}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        🎯 Probabilidad de Cierre
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={oportunidadSeleccionadaData.probabilidad || 0} 
                          sx={{ 
                            flex: 1, 
                            height: 10, 
                            bgcolor: '#e2e8f0',
                            borderRadius: 5,
                            '& .MuiLinearProgress-bar': { 
                              bgcolor: oportunidadSeleccionadaData.probabilidad > 70 ? '#10b981' : 
                                     oportunidadSeleccionadaData.probabilidad > 40 ? '#f59e0b' : '#ef4444',
                              borderRadius: 5
                            }
                          }} 
                        />
                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 40 }}>
                          {oportunidadSeleccionadaData.probabilidad || 0}%
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        📅 Cierre Estimado
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon fontSize="small" />
                        {oportunidadSeleccionadaData.fecha_cierre_estimada 
                          ? new Date(oportunidadSeleccionadaData.fecha_cierre_estimada).toLocaleDateString('es-DO')
                          : 'No definida'}
                      </Typography>
                    </Box>
                    
                    {oportunidadSeleccionadaData.competidor && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          👥 Competidor
                        </Typography>
                        <Chip
                          label={oportunidadSeleccionadaData.competidor}
                          size="small"
                          sx={{ bgcolor: '#fef3c7', color: '#92400e' }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
                
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#64748b">
                      👤 Responsable
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: '#3b82f6',
                          width: 40,
                          height: 40
                        }}
                      >
                        {oportunidadSeleccionadaData.responsable?.nombre?.charAt(0) || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {oportunidadSeleccionadaData.responsable?.nombre || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {oportunidadSeleccionadaData.responsable?.email || ''}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Información del prospecto */}
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#64748b">
                      📞 Información de Contacto
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Nombre
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {oportunidadSeleccionadaData.prospecto?.nombre || '-'}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Empresa
                        </Typography>
                        <Typography variant="body2">
                          {oportunidadSeleccionadaData.prospecto?.empresa || '-'}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Teléfono
                        </Typography>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon fontSize="small" />
                          {oportunidadSeleccionadaData.prospecto?.telefono || '-'}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Email
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon fontSize="small" />
                          {oportunidadSeleccionadaData.prospecto?.email || '-'}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          🏗️ Tipo de Proyecto
                        </Typography>
                        <Chip
                          label={oportunidadSeleccionadaData.prospecto?.proyecto_tipo || '-'}
                          size="small"
                          sx={{ bgcolor: '#e0e7ff', color: '#4f46e5' }}
                        />
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          🌐 Fuente
                        </Typography>
                        <Chip
                          label={oportunidadSeleccionadaData.prospecto?.fuente || '-'}
                          size="small"
                          sx={{ bgcolor: '#fef3c7', color: '#92400e' }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
                
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#64748b">
                      📝 Notas
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      p: 1.5, 
                      bgcolor: '#f8fafc', 
                      borderRadius: 1,
                      minHeight: 100
                    }}>
                      {oportunidadSeleccionadaData.notas || 'Sin notas adicionales'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button
            variant="outlined"
            onClick={handleOpenSeguimientoModal}
            startIcon={<AddIcon />}
            sx={{ 
              borderColor: '#3b82f6', 
              color: '#3b82f6',
              '&:hover': { bgcolor: '#eff6ff' }
            }}
            disabled={!oportunidadSeleccionadaId}
          >
            Agregar Seguimiento
          </Button>
          <Button 
            onClick={handleCloseDetalleModal} 
            variant="contained"
            sx={{ bgcolor: '#3b82f6' }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de seguimientos */}
      {oportunidadSeleccionadaId && (
        <SeguimientosModal
          open={seguimientoModalOpen}
          onClose={handleCloseSeguimientoModal}
          oportunidadId={oportunidadSeleccionadaId}
          oportunidadNombre={oportunidadSeleccionadaData?.prospecto?.nombre}
          oportunidadEmpresa={oportunidadSeleccionadaData?.prospecto?.empresa}
        />
      )}

         {/* Modal de Transición de Etapa - FALTABA ESTO */}
      <TransicionEtapaModal
        open={transicionModalOpen}
        onClose={() => {
          setTransicionModalOpen(false);
          setTransicionData({ oportunidad: null, etapaDestino: null });
        }}
        oportunidad={transicionData.oportunidad}
        etapaDestino={transicionData.etapaDestino}
        onTransicionCompleta={() => {
          // Recargar oportunidades después de mover
          cargarOportunidades();
          setTransicionModalOpen(false);
          setTransicionData({ oportunidad: null, etapaDestino: null });
          toast.success('✅ Etapa actualizada correctamente');
        }}
      />
    </Box>
  );
};

export default PipelineKanban;